import { resolveAssetImageSrc } from './assetImageResolve';
import { extractAssetOwnerId, extractSessionOwnerIdFromAccessToken } from './assetOwnerMatch';
import { ownerLogoFromRecord } from './ownerLogoFromRecord';

export const DEFAULT_ASSET_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/placy-logo.png`;

const ASSET_OWNER_NESTED_KEYS = [
  'asset_owner',
  'owner',
  'private_asset_owner',
  'business_asset_owner',
] as const;

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function resolveLogoWire(raw: string | null | undefined): string | null {
  if (!raw?.trim()) {
    return null;
  }
  return resolveAssetImageSrc(raw.trim()) ?? raw.trim();
}

export type AssetRecordLogoOptions = {
  /** Owner business logo for the signed-in session (from GET /profile). */
  sessionOwnerLogoSrc?: string | null;
  sessionAccessToken?: string | null;
  /** Use when the parent already matched the asset to the session owner. */
  fallbackLogoSrc?: string | null;
};

export function resolveAssetRecordLogoSrc(
  record: Record<string, unknown>,
  options?: AssetRecordLogoOptions,
): string {
  for (const key of ASSET_OWNER_NESTED_KEYS) {
    const nested = record[key];
    if (!isPlainObject(nested)) {
      continue;
    }
    const fromNested = resolveLogoWire(ownerLogoFromRecord(nested));
    if (fromNested) {
      return fromNested;
    }
  }

  const fromTopLevel = resolveLogoWire(ownerLogoFromRecord(record));
  if (fromTopLevel) {
    return fromTopLevel;
  }

  const fallback = resolveLogoWire(options?.fallbackLogoSrc);
  if (fallback) {
    return fallback;
  }

  const sessionLogo = resolveLogoWire(options?.sessionOwnerLogoSrc);
  if (sessionLogo) {
    const assetOwnerId = extractAssetOwnerId(record);
    const sessionOwnerId = extractSessionOwnerIdFromAccessToken(options?.sessionAccessToken);
    if (assetOwnerId && sessionOwnerId && assetOwnerId === sessionOwnerId) {
      return sessionLogo;
    }
  }

  return DEFAULT_ASSET_LOGO_SRC;
}
